
import { lazy, Suspense, isValidElement } from "react";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { coreRoutes } from "./core";
import { caseStudyRoutes } from "./caseStudies";
import { serviceRoutes } from "./services";
import { cybersecurityRoutes } from "./cybersecurity";
import { specializationRoutes } from "./specializations";
import { adminRoutes } from "./admin";
import { landingRoutes } from "./landing";
import { toolsRoutes } from "./tools";
import { resourcesRoutes } from "./resources";
import { AppRoute } from "@/types/routes";

// Lazy load security guide
const SecurityGuide = lazy(() => import("@/pages/resources/guides/SecurityGuide"));

// Helper function to check if element is already wrapped in Suspense
const isSuspenseWrapped = (element: React.ReactNode): boolean => {
  return isValidElement(element) && 
         element.type === Suspense;
};

// Cast admin routes and tools routes as AppRoute to match type structure
const adminRoutesTyped = adminRoutes as unknown as AppRoute;
const toolsRoutesTyped = toolsRoutes as unknown as AppRoute;

// Define all routes
export const routes: AppRoute[] = [
  ...coreRoutes,
  ...caseStudyRoutes,
  ...serviceRoutes,
  ...cybersecurityRoutes,
  ...specializationRoutes,
  ...resourcesRoutes,
  // Cast to AppRoute
  toolsRoutesTyped,
  // Cast to AppRoute
  adminRoutesTyped,
  ...landingRoutes,
  // Add security guide route
  {
    path: "/resources/security-guide",
    element: (
      <Suspense fallback={<LoadingSpinner fullPage />}>
        <SecurityGuide />
      </Suspense>
    ),
  },
].map(route => {
  // Skip routes that don't have elements
  if (!('element' in route)) {
    return route;
  }
  
  // If the route already has a Suspense wrapper, don't wrap it again
  if (isSuspenseWrapped(route.element)) {
    return route;
  }
  
  // Otherwise, wrap the element with Suspense
  return {
    ...route,
    element: (
      <Suspense fallback={<LoadingSpinner fullPage />}>
        {route.element}
      </Suspense>
    )
  };
});
